import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { formatCurrency, fromBaseCurrency, toBaseCurrency, API_URL } from "../../lib/utils";
import { PayLinesEditor } from "./PayLinesEditor";
import { monthLabel } from "../../lib/payslipPrint";

const toLines = (arr) => (arr || []).map((l) => ({ label: l.label, amount: String(fromBaseCurrency(l.amount)) }));
const sum = (lines) => lines.reduce((a, l) => a + toBaseCurrency(l.amount), 0);

export const ProcessPayrollDialog = ({ open, onOpenChange, employee, month, headers, onSaved }) => {
  const [earnings, setEarnings] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [includeAttendance, setIncludeAttendance] = useState(true);
  const att = employee?.attendance;
  const attTotal = att?.deduction_total || 0;

  useEffect(() => {
    if (!open || !employee) return;
    const p = employee.payroll;
    // Linhas automáticas (Faltas/Atrasos) ficam fora do editor: o servidor recalcula-as quando a opção está activa
    setEarnings(toLines(p?.earnings)); setDeductions(toLines((p?.deductions_items || []).filter((l) => !/^(Faltas|Atrasos) \(/.test(l.label)))); setNotes(p?.notes || "");
    setIncludeAttendance(true);
  }, [open, employee]);

  const base = employee?.base_salary || 0;
  const gross = base + sum(earnings);
  const totalDed = sum(deductions) + (includeAttendance ? attTotal : 0);
  const net = gross - totalDed;

  const earnQuick = [
    { id: "alimentacao", label: "Subsídio de alimentação" }, { id: "transporte", label: "Subsídio de transporte" },
    { id: "horas-extra", label: "Horas extraordinárias" }, { id: "comissoes", label: "Comissões" },
    { id: "premio", label: "Prémio de desempenho" }, { id: "ferias", label: "Subsídio de férias" }, { id: "natal", label: "Subsídio de Natal" },
  ];
  const dedQuick = [
    { id: "inss", label: "INSS (3%)", compute: () => (fromBaseCurrency(gross) * 0.03).toFixed(2) },
    { id: "irt", label: "IRT" }, { id: "adiantamento", label: "Adiantamento" }, { id: "emprestimo", label: "Empréstimo" },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/hr/payroll`, {
        user_id: employee.user_id, month, base_salary: base, notes, include_attendance: includeAttendance,
        earnings: earnings.map((l) => ({ label: l.label, amount: toBaseCurrency(l.amount) })),
        deductions_items: deductions.map((l) => ({ label: l.label, amount: toBaseCurrency(l.amount) })),
      }, { headers, withCredentials: true });
      toast.success(employee.payroll ? "Recibo atualizado" : "Salário processado — recibo gerado");
      onOpenChange(false); onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao processar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="process-payroll-dialog">
        <DialogHeader><DialogTitle>{employee?.payroll ? "Editar" : "Processar"} Vencimento — {employee?.full_name || employee?.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex justify-between flex-wrap gap-2 text-sm rounded-lg bg-muted/40 p-3">
            <span>Período: <b>{monthLabel(month)}</b></span>
            <span>Salário base: <b className="font-mono" data-testid="process-base">{formatCurrency(base)}</b></span>
          </div>
          {!employee?.inss_number && <p className="text-xs text-amber-600" data-testid="inss-missing-warning">⚠ Este trabalhador não tem Nº INSS registado — preencha em "Dados RH" para um recibo completo.</p>}
          <PayLinesEditor title="Remunerações (subsídios, comissões, prémios, horas extra)" lines={earnings} onChange={setEarnings} quick={earnQuick} testPrefix="earn" tone="text-emerald-600" />
          {att && (
            <label className="flex items-start gap-2 rounded-lg border p-3 text-sm cursor-pointer" data-testid="payroll-attendance-block">
              <input type="checkbox" className="mt-1" checked={includeAttendance} onChange={(e) => setIncludeAttendance(e.target.checked)} data-testid="payroll-include-attendance" />
              <span className="flex-1">
                <span className="font-medium">Descontar faltas e atrasos do mês automaticamente</span>
                <span className="block text-xs text-muted-foreground">
                  {att.present_days} dia(s) presente · {att.absences} falta(s){att.justified_absences ? ` (+${att.justified_absences} justificada(s))` : ""} · {att.late_count} atraso(s){att.late_minutes ? ` (${att.late_minutes} min)` : ""}
                </span>
                <span className="block text-xs font-mono text-red-600" data-testid="payroll-attendance-total">
                  {att.absences ? `Faltas ${att.absences} × ${formatCurrency(att.per_absence)} = −${formatCurrency(att.deduction_absences)}` : "Sem faltas"}{att.deduction_late ? ` · Atrasos −${formatCurrency(att.deduction_late)}` : ""}
                </span>
              </span>
            </label>
          )}
          <PayLinesEditor title="Outros descontos e deduções" lines={deductions} onChange={setDeductions} quick={dedQuick} testPrefix="ded" tone="text-red-600" />
          <div className="space-y-1"><Label className="text-xs">Observações (aparecem no recibo)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex.: inclui 6h extra do dia 12; adiantamento acordado a 02/06" data-testid="payroll-notes-input" /></div>
          <div className="grid grid-cols-3 gap-2 text-sm rounded-lg border p-3">
            <div><p className="text-xs text-muted-foreground">Bruto</p><p className="font-mono font-semibold" data-testid="process-gross">{formatCurrency(gross)}</p></div>
            <div><p className="text-xs text-muted-foreground">Descontos</p><p className="font-mono font-semibold text-red-600" data-testid="process-deductions">−{formatCurrency(totalDed)}</p></div>
            <div><p className="text-xs text-muted-foreground">Líquido</p><p className={`font-mono font-bold ${net < 0 ? "text-red-600" : ""}`} data-testid="process-net">{formatCurrency(net)}</p></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving || net < 0} data-testid="confirm-process-btn">{saving ? "A processar..." : employee?.payroll ? "Guardar alterações" : "Processar e gerar recibo"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
